import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowRight,
  faBolt,
  faChevronLeft,
  faChevronRight,
  faCode,
  faGamepad,
  faHeart,
  faLightbulb,
  faMobileAlt,
  faPlay,
  faRocket,
} from "@fortawesome/free-solid-svg-icons";
import {
  faApple,
  faGooglePlay,
  faInstagram,
  faYoutube,
} from "@fortawesome/free-brands-svg-icons";
import logo from "assets/img/illustrations/nodra-only-logo.png";
import outOfSight from "assets/img/generic/out-of-sight.png";
import outOfSightVideo from "assets/videos/outofsight/outofsight-video.mp4";
import echoTrace from "assets/img/echo-trace/echo-trace.png";
import echoTraceVideo from "assets/videos/echotrace/echo.MP4";
import orbitUpVideo from "assets/videos/orbitup/orbit-up.mp4";
import "assets/css/landing.css";

const scrollTo = (id) =>
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
//
const GameCarousel = ({ children }) => {
  const slides = React.Children.toArray(children);
  const [activeSlide, setActiveSlide] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  const showSlide = (index) => {
    setActiveSlide((index + slides.length) % slides.length);
  };

  useEffect(() => {
    if (isPaused) return undefined;

    const timer = window.setInterval(() => {
      setActiveSlide((current) => (current + 1) % slides.length);
    }, 7000);

    return () => window.clearInterval(timer);
  }, [isPaused, slides.length]);

  return (
    <div
      className="game-carousel"
      aria-roledescription="carousel"
      aria-label="Nodra Studios oyunları"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      onFocus={() => setIsPaused(true)}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) {
          setIsPaused(false);
        }
      }}
      onKeyDown={(event) => {
        if (event.key === "ArrowLeft") showSlide(activeSlide - 1);
        if (event.key === "ArrowRight") showSlide(activeSlide + 1);
      }}
    >
      <div className="game-carousel-viewport">
        <div
          className="game-carousel-track"
          style={{ transform: `translateX(-${activeSlide * 100}%)` }}
        >
          {slides.map((slide, index) => (
            <div
              className="game-slide"
              key={index}
              aria-hidden={index !== activeSlide}
            >
              {slide}
            </div>
          ))}
        </div>
      </div>
      <div className="game-carousel-controls">
        <button
          type="button"
          className="carousel-arrow"
          onClick={() => showSlide(activeSlide - 1)}
          aria-label="Önceki oyunu göster"
        >
          <FontAwesomeIcon icon={faChevronLeft} />
        </button>
        <div className="carousel-dots" aria-label="Oyun seçimi">
          {slides.map((_, index) => (
            <button
              type="button"
              key={index}
              className={index === activeSlide ? "active" : ""}
              onClick={() => showSlide(index)}
              aria-label={`${index + 1}. oyunu göster`}
              aria-current={index === activeSlide ? "true" : undefined}
            />
          ))}
        </div>
        <span className="carousel-position" aria-live="polite">
          {String(activeSlide + 1).padStart(2, "0")} /{" "}
          {String(slides.length).padStart(2, "0")}
        </span>
        <button
          type="button"
          className="carousel-arrow"
          onClick={() => showSlide(activeSlide + 1)}
          aria-label="Sonraki oyunu göster"
        >
          <FontAwesomeIcon icon={faChevronRight} />
        </button>
      </div>
    </div>
  );
};

export const Landing = () => (
  <main className="studio-landing">
    <nav className="studio-nav" aria-label="Ana navigasyon">
      <a
        className="studio-brand"
        href="#top"
        aria-label="Nodra Studios ana sayfa"
      >
        <span className="brand-logo">
          <img src={logo} alt="Nodra Studios logosu" />
        </span>
        <span>Nodra Studios</span>
      </a>
      <div className="studio-nav-links">
        <button type="button" onClick={() => scrollTo("games")}>
          Oyunlar
        </button>
        <button type="button" onClick={() => scrollTo("process")}>
          Süreç
        </button>
        <button type="button" onClick={() => scrollTo("about")}>
          Hakkımızda
        </button>
        <a
          className="nav-social"
          href="https://instagram.com/nodrastudios"
          target="_blank"
          rel="noreferrer"
          aria-label="Nodra Studios Instagram hesabı"
        >
          <FontAwesomeIcon icon={faInstagram} />
        </a>
      </div>
    </nav>

    <header className="studio-hero" id="top">
      <div className="hero-glow hero-glow-one" />
      <div className="hero-glow hero-glow-two" />
      <div className="hero-grid" />
      <div className="hero-content">
        <span className="eyebrow">
          <i /> Bağımsız mobil oyun stüdyosu
        </span>
        <h1>
          Küçük ekranlar için
          <br />
          <em>büyük dünyalar.</em>
        </h1>
        <p>
          Oynaması kolay, bırakması zor mobil deneyimler tasarlıyoruz. Tüm
          oyunlarımızı, güncellemeleri ve yeni maceraları burada keşfedin.
        </p>
        <div className="hero-actions">
          <button
            className="primary-action"
            type="button"
            onClick={() => scrollTo("games")}
          >
            Oyunları keşfet <FontAwesomeIcon icon={faArrowRight} />
          </button>
          <button
            className="secondary-action disabled-action"
            type="button"
            disabled
            title="YouTube kanalımız yakında açılacak"
          >
            <span>
              <FontAwesomeIcon icon={faPlay} />
            </span>{" "}
            YouTube yakında
          </button>
        </div>
      </div>
      <div className="hero-orbit" aria-hidden="true">
        <div className="orbit-ring orbit-ring-one" />
        <div className="orbit-ring orbit-ring-two" />
        <div className="orbit-core">
          <FontAwesomeIcon icon={faGamepad} />
        </div>
        <span className="orbit-satellite satellite-one">
          <i className="orbit-dot dot-one" />
        </span>
        <span className="orbit-satellite satellite-two">
          <i className="orbit-dot dot-two" />
        </span>
        <span className="orbit-satellite satellite-three">
          <i className="orbit-dot dot-three" />
        </span>
      </div>
    </header>

    <section className="value-strip" aria-label="Stüdyo değerleri">
      <div>
        <FontAwesomeIcon icon={faMobileAlt} />
        <span>
          <strong>Mobil öncelikli</strong>
          <small>Her an, her yerde oyun</small>
        </span>
      </div>
      <div>
        <FontAwesomeIcon icon={faHeart} />
        <span>
          <strong>Özenle tasarlandı</strong>
          <small>Her detayda oyuncu deneyimi</small>
        </span>
      </div>
      <div>
        <FontAwesomeIcon icon={faBolt} />
        <span>
          <strong>Hızlı ve akıcı</strong>
          <small>Bekletmeyen performans</small>
        </span>
      </div>
    </section>

    <section className="games-section" id="games">
      <div className="section-heading">
        <div>
          <span className="section-kicker">OYUNLARIMIZ</span>
          <h2>
            Bir sonraki favorin
            <br />
            burada olabilir.
          </h2>
        </div>
        <p>
          Her biri kendine özgü dünyalara, mekaniklere ve hikâyelere sahip
          oyunlarımızla tanışın.
        </p>
      </div>
      <GameCarousel>
        <article className="featured-game">
          <div className="featured-visual">
            <video
              autoPlay
              muted
              loop
              playsInline
              controls
              preload="metadata"
              poster={outOfSight}
              aria-label="Out of Sight oynanış videosu"
            >
              <source src={outOfSightVideo} type="video/mp4" />
              Tarayıcınız video etiketini desteklemiyor.
            </video>
            <span className="featured-badge">
              <i /> YAYINDA
            </span>
          </div>
          <div className="featured-copy">
            <span className="game-genre">KAÇIŞ · RUNNER · MOBİL</span>
            <h3>Out of Sight</h3>
            <h4>Kaçış başladı!</h4>
            <p className="featured-lead">
              Gardiyanlara ve kameralara yakalanma, anahtarı al ve kapıya koş.
            </p>
            <p>
              Gardiyan peşinde. Çıkış ise sandığından daha uzakta. Hızını,
              reflekslerini ve doğru karar verme yeteneğini test eden tempolu
              bir kaçış seni bekliyor.
            </p>
            <div className="featured-tags">
              <span>Kolay kontroller</span>
              <span>Hızlı oynanış</span>
              <span>Zorlu rotalar</span>
            </div>
            <a
              className="app-store-link"
              href="https://apps.apple.com/tr/app/out-of-sight/id6800069300"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Out of Sight'ı App Store'dan indir (yeni sekmede açılır)"
            >
              <FontAwesomeIcon icon={faApple} />
              <span>
                <small>App Store'dan</small>İndir
              </span>
            </a>
            <details className="game-details">
              <summary>
                Oyunu daha yakından tanı <FontAwesomeIcon icon={faArrowRight} />
              </summary>
              <div className="details-content">
                <p>
                  Runner'ı kontrol et, engellerden kaç, gardiyana yakalanmadan
                  doğru rotayı bul ve çıkışa ulaş! Her bölümde farklı bir kaçış
                  rotası ve yeni bir mücadele var. Hızlı hareket etmek önemli;
                  ancak yanlış bir hamle bütün kaçış planını bozabilir.
                </p>
                <h5>Özellikler</h5>
                <ul>
                  <li>Basit ve kolay öğrenilen kontroller</li>
                  <li>Hızlı ve akıcı oynanış</li>
                  <li>Refleks ve zamanlama odaklı bölümler</li>
                  <li>Giderek zorlaşan kaçış mücadeleleri</li>
                  <li>Eğlenceli runner ve gardiyan kovalamacası</li>
                  <li>Kısa sürede oynanabilen bölümler</li>
                  <li>Sade ve renkli görsel tasarım</li>
                </ul>
                <p className="details-closing">
                  Gardiyan seni yakalamadan önce çıkışı bulabilecek misin?
                  Rotanı belirle, hızlı hareket et ve kaçışı tamamla!
                </p>
              </div>
            </details>
          </div>
        </article>
        <article className="featured-game echo-trace-game">
          <div className="featured-visual echo-trace-visual">
            <video
              autoPlay
              muted
              loop
              playsInline
              controls
              preload="metadata"
              poster={echoTrace}
              aria-label="Echo Trace oynanış videosu"
            >
              <source src={echoTraceVideo} type="video/mp4" />
              Tarayıcınız video etiketini desteklemiyor.
            </video>
            <span className="featured-badge echo-trace-badge">
              <i /> YENİ OYUN
            </span>
          </div>
          <div className="featured-copy">
            <span className="game-genre">HAFIZA · BULMACA · MOBİL</span>
            <h3>Echo Trace</h3>
            <h4>Her sinyal yeni bir test.</h4>
            <p className="featured-lead">
              Sinyali takip et, deseni hatırla ve izini bırak! Hafızanı zorla,
              serini büyüt ve küresel liderlik tablosunda zirveye tırman.
            </p>
            <p>
              Echo Trace, hafızanı ve dikkatini test eden hızlı tempolu bir
              desen hafıza oyunudur.
            </p>
            <div className="featured-tags">
              <span>Desen hafızası</span>
              <span>Küresel liderlik</span>
              <span>Neon atmosfer</span>
            </div>
            <a
              className="app-store-link"
              href="https://apps.apple.com/tr/app/echo-trace/id6803668282"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Echo Trace'i App Store'dan indir (yeni sekmede açılır)"
            >
              <FontAwesomeIcon icon={faApple} />
              <span>
                <small>App Store'dan</small>İndir
              </span>
            </a>
            <details className="game-details echo-trace-details">
              <summary>
                Oyunu daha yakından tanı <FontAwesomeIcon icon={faArrowRight} />
              </summary>
              <div className="details-content">
                <p>
                  Sinyaller belirir. Desen kaybolur. Geriye yalnızca hafızan
                  kalır.
                </p>
                <p>
                  Gösterilen sinyalleri dikkatle izle, doğru konumları aklında
                  tut ve deseni eksiksiz şekilde yeniden oluştur. Başarılı
                  oldukça ilerle, daha yüksek puanlara ulaş ve sınırlarını
                  zorla.
                </p>
                <h5>Özellikler</h5>
                <ul>
                  <li>Sinyalleri gözlemle ve doğru deseni hatırla</li>
                  <li>Hafızanı ve dikkatini zorlayan seviyelerde ilerle</li>
                  <li>Başarılı hamlelerle yüksek skorlar elde et</li>
                  <li>Serini koru ve performansını geliştir</li>
                  <li>Küresel liderlik tablosunda diğer oyuncularla yarış</li>
                  <li>Minimal, karanlık ve neon temalı arayüz</li>
                  <li>Ses ve dokunsal geri bildirim desteği</li>
                  <li>Hızlı ve tekrar oynanabilir oyun yapısı</li>
                </ul>
                <p className="details-closing">
                  Ne kadarını hatırlayabilirsin? Echo Trace'i indir, sinyali
                  yakala ve adını zirveye yazdır.
                </p>
              </div>
            </details>
          </div>
        </article>
        <article className="featured-game orbit-up-game">
          <div className="featured-visual orbit-up-visual">
            <video
              autoPlay
              muted
              loop
              playsInline
              controls
              preload="metadata"
              aria-label="Orbit Up oynanış videosu"
            >
              <source src={orbitUpVideo} type="video/mp4" />
              Tarayıcınız video etiketini desteklemiyor.
            </video>
            <span className="featured-badge orbit-up-badge">
              <i /> YENİ OYUN
            </span>
          </div>
          <div className="featured-copy">
            <span className="game-genre">ARCADE · REFLEKS · MOBİL</span>
            <h3>Orbit Up</h3>
            <h4>Uzayın sınırlarının ötesine tırman!</h4>
            <p className="featured-lead">
              Zıpla, yörüngelere tutun, kombonu oluştur ve uzayın sınırlarının
              ötesine ulaş! 🚀
            </p>
            <p>
              Orbit Up; zamanlamanı, reflekslerini ve risk alma becerini sınayan
              hızlı tempolu bir 2D arcade oyunu.
            </p>
            <div className="featured-tags">
              <span>Tek dokunuşla kontrol</span>
              <span>Kombolar ve skor</span>
              <span>Küresel liderlik</span>
            </div>
            <div className="store-links">
              <a
                className="app-store-link"
                href="https://apps.apple.com/tr/app/orbit-up/id6805603169"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Orbit Up'ı App Store'dan indir (yeni sekmede açılır)"
              >
                <FontAwesomeIcon icon={faApple} />
                <span>
                  <small>App Store'dan</small>İndir
                </span>
              </a>
              <a
                className="app-store-link google-play-link"
                href="https://play.google.com/store/apps/details?id=com.orbitup.game&hl=tr"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Orbit Up'ı Google Play'den indir (yeni sekmede açılır)"
              >
                <FontAwesomeIcon icon={faGooglePlay} />
                <span>
                  <small>Google Play'den</small>İndir
                </span>
              </a>
            </div>
            <details className="game-details orbit-up-details">
              <summary>
                Oyunu daha yakından tanı <FontAwesomeIcon icon={faArrowRight} />
              </summary>
              <div className="details-content">
                <p>
                  <strong>Ne kadar yükseğe çıkabilirsin? 🌍</strong> Kırmızı
                  üçgeni kontrol et, 180 derecelik yörüngeler arasında zıpla ve
                  her hamlede biraz daha yükseğe tırman. Tek bir hata koşunu
                  bitirebilir; doğru anı yakala!
                </p>
                <p>
                  Yolculuğun Dünya'da başlar. Atmosferi geride bırak, uzaya açıl
                  ve sürekli değişen kozmik ortamları keşfet. Yükseldikçe meydan
                  okuma da büyür. 🌌
                </p>
                <h5>Öne çıkanlar ✨</h5>
                <ul>
                  <li>Basit ve hızlı tepki veren tek dokunuşlu oynanış 👆</li>
                  <li>Sonsuz dikey arcade deneyimi 🧗</li>
                  <li>PERFECT ve BULLSEYE ile hassas zamanlama sistemi 🎯</li>
                  <li>Kombo kurarak skorunu katlama 🔥</li>
                  <li>Enerji çekirdeklerini topla, BOOST'u hazırla ⚡</li>
                  <li>Tek hamlede yörüngeleri aşan güçlü dikey hızlanma 🚀</li>
                  <li>Giderek zorlaşan hareketli engellerden kaçın 🛰️</li>
                  <li>Dünya'dan derin uzaya uzanan dinamik ortamlar 🌠</li>
                  <li>Küresel liderlik tablosunda dünyayla yarış 🏆</li>
                  <li>Yüksek skor ve maksimum yükseklik rekorlarını kır 📈</li>
                  <li>
                    Koşuna devam etmek için isteğe bağlı ödüllü reklamlar 🎁
                  </li>
                </ul>
                <p className="details-closing">
                  Zıpla. Tırman. Hayatta kal. Bir sonraki yörünge seni bekliyor.
                  Dünyaya ne kadar yükseğe çıkabileceğini göster! ✨
                </p>
              </div>
            </details>
          </div>
        </article>
      </GameCarousel>
      <aside className="upcoming-note">
        <div className="upcoming-copy">
          <span className="section-kicker">YENİ PROJELER</span>
          <h3>
            Yeni oyunlar
            <br />
            üzerinde çalışıyoruz.
          </h3>
          <p>
            Henüz duyurmadığımız projelerimizi geliştirmeye devam ediyoruz. Yeni
            oyunlar, güncellemeler ve geliştirme notları hazır olduğunda
            yalnızca resmi kanallarımızdan paylaşılacak.
          </p>
          <a
            href="https://instagram.com/nodrastudios"
            target="_blank"
            rel="noreferrer"
          >
            <FontAwesomeIcon icon={faInstagram} /> Duyurular için @nodrastudios
          </a>
        </div>
        <div className="upcoming-signal" aria-hidden="true">
          <span className="signal-ring signal-ring-one" />
          <span className="signal-ring signal-ring-two" />
          <span className="signal-core">
            <FontAwesomeIcon icon={faRocket} />
          </span>
          <small>GELİŞTİRME AŞAMASINDA</small>
        </div>
      </aside>
    </section>

    <section className="process-section" id="process">
      <div className="process-intro">
        <span className="section-kicker">NASIL ÜRETİYORUZ?</span>
        <h2>
          Bir fikirden,
          <br />
          <em>oyuncunun cebine.</em>
        </h2>
        <p>
          Her oyunu küçük bir fikirle başlatıyor, oynanabilir prototiplerle
          geliştiriyor ve gerçek oyuncu geri bildirimleriyle büyütüyoruz.
        </p>
      </div>
      <div className="process-list">
        <article>
          <span className="process-index">01</span>
          <div className="process-icon">
            <FontAwesomeIcon icon={faLightbulb} />
          </div>
          <div>
            <h3>Fikri bul</h3>
            <p>
              Basit, güçlü ve tekrar oynama isteği uyandıran bir oyun fikri
              oluşturuyoruz.
            </p>
          </div>
        </article>
        <article>
          <span className="process-index">02</span>
          <div className="process-icon">
            <FontAwesomeIcon icon={faCode} />
          </div>
          <div>
            <h3>Oynanabilir hale getir</h3>
            <p>
              Temel mekaniği hızla prototipliyor, en eğlenceli haline ulaşana
              kadar test ediyoruz.
            </p>
          </div>
        </article>
        <article>
          <span className="process-index">03</span>
          <div className="process-icon">
            <FontAwesomeIcon icon={faRocket} />
          </div>
          <div>
            <h3>Yayınla ve geliştir</h3>
            <p>
              Oyunu oyuncularla buluşturuyor, geri bildirimlerle yeni içerikler
              ekliyoruz.
            </p>
          </div>
        </article>
      </div>
    </section>

    <section className="about-section" id="about">
      <div className="about-mark">N</div>
      <div className="about-copy">
        <span className="section-kicker">NODRA STUDIOS</span>
        <h2>
          Oyuncular için,
          <br />
          oyuncular tarafından.
        </h2>
        <p>
          Nodra Studios, eğlenceli fikirleri herkesin cebinde taşıyabileceği
          oyunlara dönüştüren bağımsız bir mobil oyun stüdyosudur. Yeni
          projelerimizi ve geliştirme sürecini sosyal kanallarımızdan takip
          edin.
        </p>
        <div className="social-links">
          <button
            className="disabled-action"
            type="button"
            disabled
            title="YouTube kanalımız yakında açılacak"
          >
            <FontAwesomeIcon icon={faYoutube} /> YouTube yakında
          </button>
          <a
            href="https://instagram.com/nodrastudios"
            target="_blank"
            rel="noreferrer"
          >
            <FontAwesomeIcon icon={faInstagram} /> @nodrastudios
          </a>
        </div>
      </div>
    </section>

    <section className="follow-section">
      <div className="follow-glow" />
      <span className="section-kicker">YENİLİKLERDEN HABERDAR OL</span>
      <h2>
        Yeni bir dünya açıldığında
        <br />
        ilk sen öğren.
      </h2>
      <p>
        Oyun duyuruları, geliştirme günlükleri ve kamera arkası içerikler için
        bizi takip et.
      </p>
      <div className="follow-actions">
        <button
          className="primary-action disabled-action"
          type="button"
          disabled
          title="YouTube kanalımız yakında açılacak"
        >
          <FontAwesomeIcon icon={faYoutube} /> YouTube yakında
        </button>
        <a
          className="follow-instagram"
          href="https://instagram.com/nodrastudios"
          target="_blank"
          rel="noreferrer"
        >
          <FontAwesomeIcon icon={faInstagram} /> @nodrastudios
        </a>
      </div>
    </section>

    <footer className="studio-footer">
      <a className="studio-brand" href="#top">
        <span className="brand-logo">
          <img src={logo} alt="Nodra Studios logosu" />
        </span>
        <span>Nodra Studios</span>
      </a>
      <p>© {new Date().getFullYear()} Nodra Studios. Tüm hakları saklıdır.</p>
      <a href="mailto:info@nodrastudios.com">info@nodrastudios.com</a>
    </footer>
  </main>
);
